import { type SwapData } from 'components/tabs/types'
import { getStatusDisplayText } from '../components/badges/status'
import { formatDate } from '../lib/transaction-utils'
import {
  calculateDisplayFeeBps,
  calculateLpInclusiveAmountTraded,
  calculateSwapPrice,
  parseFormattedAmount,
} from '../lib/swap-fee-display'
import { type TransactionData } from 'components/tabs/TransactionsTabView'

const buildHeaders = (msbName: string) => [
  'ID',
  'Group',
  'Company',
  'User',
  'Type',
  'Amount',
  'Currency',
  `${msbName} Fee (%)`,
  `${msbName} Fee (Amount)`,
  'Amount Sent Minus Fee',
  'From Currency',
  'To',
  'To Amount',
  'To Currency',
  'Price',
  'Pairing',
  'Status',
  'Date',
  'LP ID',
]

const capitalizeMsb = (msb: string) => msb.charAt(0).toUpperCase() + msb.slice(1).toLowerCase()

const downloadCSV = (content: string, filename: string) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const swapsExportToCSV = (swaps: SwapData[], msbName: string) => {
  const headers = buildHeaders(capitalizeMsb(msbName))
  const csvData = swaps.map((swap) => {
    const fromAmt = parseFormattedAmount(swap.fromAmount)
    const toAmt = parseFormattedAmount(swap.toAmount)
    const displayFeeBps = calculateDisplayFeeBps({
      swapGroupBps: swap.swapGroupBps,
      swapValiantBps: swap.swapValiantBps,
    })
    const feePct = displayFeeBps / 100
    const feeAmount = fromAmt * (displayFeeBps / 10000)
    const amountTraded = calculateLpInclusiveAmountTraded({
      fromAmount: fromAmt,
      swapGroupBps: swap.swapGroupBps,
      swapValiantBps: swap.swapValiantBps,
    })
    const price = calculateSwapPrice({ toAmount: toAmt, amountTraded })
    const fromDp = swap.fromMint.displayDecimals ?? 6
    const toDp = swap.toMint.displayDecimals ?? 6

    return [
      swap.id,
      swap.group,
      swap.company,
      swap.requestedBy || '-',
      'Swap',
      swap.fromAmount,
      swap.fromMint.symbol,
      `${feePct}%`,
      `${feeAmount.toFixed(fromDp)} ${swap.fromMint.symbol}`,
      amountTraded.toFixed(fromDp),
      swap.fromMint.symbol,
      '>>>',
      swap.toAmount,
      swap.toMint.symbol,
      amountTraded > 0 ? price.toFixed(toDp) : 'N/A',
      `${swap.toMint.symbol}/${swap.fromMint.symbol}`,
      getStatusDisplayText(swap.status, 'TRADE'),
      formatDate(swap.completedAt || swap.decisionAt || swap.createdAt || ''),
      swap.lpId.toString(),
    ]
  })

  const csvContent = [headers, ...csvData]
    .map((row) => row.map((cell: string) => `"${cell}"`).join(','))
    .join('\n')

  downloadCSV(csvContent, `swaps-export-${new Date().toISOString().split('T')[0]}.csv`)
}

const transactionExportToCSV = (transactions: TransactionData[], msbName: string) => {
  const headers = buildHeaders(capitalizeMsb(msbName))
  const csvData = transactions.map((trade) => {
    const isSwap = trade.type === 'TRADE'
    const groupBps = isSwap && trade.swapGroupBps != null ? trade.swapGroupBps : null
    const valiantBps = isSwap && trade.swapValiantBps != null ? trade.swapValiantBps : 0
    const displayFeeBps =
      groupBps != null ? calculateDisplayFeeBps({ swapGroupBps: groupBps, swapValiantBps: valiantBps }) : null
    const fromAmt = isSwap ? parseFormattedAmount(trade.transaction.from || '0') : 0
    const toAmt = isSwap ? parseFormattedAmount(trade.transaction.to || '0') : 0
    const feeAmount = displayFeeBps != null ? fromAmt * (displayFeeBps / 10000) : null
    const amountTraded =
      groupBps != null
        ? calculateLpInclusiveAmountTraded({
            fromAmount: fromAmt,
            swapGroupBps: groupBps,
            swapValiantBps: valiantBps,
          })
        : null
    const price =
      amountTraded != null ? calculateSwapPrice({ toAmount: toAmt, amountTraded }) : 0
    const fromCurrency = isSwap
      ? trade.transaction.fromCurrency || ''
      : trade.transaction.currency || ''

    return [
      trade.id,
      trade.group,
      trade.company,
      isSwap ? trade.user || '' : '',
      isSwap ? 'Swap' : 'Deposit',
      isSwap ? trade.transaction.from || '' : trade.transaction.amount || '',
      fromCurrency,
      displayFeeBps != null ? `${displayFeeBps / 100}%` : 'N/A',
      feeAmount != null ? `${feeAmount.toFixed(6)} ${fromCurrency}` : 'N/A',
      amountTraded != null ? amountTraded.toFixed(6) : 'N/A',
      isSwap ? trade.transaction.fromCurrency || '' : trade.transaction.currency || '',
      '>>>',
      isSwap ? trade.transaction.to || '' : 'N/A',
      isSwap ? trade.transaction.toCurrency || '' : 'N/A',
      amountTraded != null && amountTraded > 0 ? price.toFixed(6) : 'N/A',
      isSwap ? `${trade.transaction.toCurrency}/${trade.transaction.fromCurrency}` : 'N/A',
      getStatusDisplayText(trade.depositStatus || trade.swapStatus || '', trade.type),
      formatDate(trade.finalizedDate || trade.approvedDate || trade.createdDate || ''),
      trade.lpId.toString(),
    ]
  })

  const csvContent = [headers, ...csvData]
    .map((row) => row.map((cell: string) => `"${cell}"`).join(','))
    .join('\n')

  downloadCSV(csvContent, `transactions-export-${new Date().toISOString().split('T')[0]}.csv`)
}

export { swapsExportToCSV, transactionExportToCSV }
